import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { ICellRendererParams } from "@ag-grid-community/core";

import { EntityType } from "@mapmycustomers/shared/enum";
import { getPreviewEntityUniqueElementId } from "@mapmycustomers/shared/util/browser";

import ButtonLink from "@app/component/ButtonLink";
import { showEntityView } from "@app/store/entityView/actions";
import { CompanyFieldName } from "@app/util/fieldModel/CompanyFieldModel";
import { dateFormatter } from "@app/util/fieldModel/impl/fieldUtil";

interface Props extends ICellRendererParams {
  showEntityView: typeof showEntityView;
}

const ActivityAnnotationLinkCellRenderer: React.FC<Props> = ({
  column,
  node,
  showEntityView,
  value,
}) => {
  const dateToDisplay = dateFormatter(node.data, value);
  const activityType = column.getColId();

  const activity = useMemo(() => {
    if (activityType === CompanyFieldName.NEXT_ACTIVITY) {
      return node.data.upcomingActivity;
    } else {
      return node.data.lastCompletedActivity;
    }
  }, [node, activityType]);

  const handleSelect = useCallback(
    (ref) => {
      showEntityView({
        elementId: getPreviewEntityUniqueElementId(ref.target),
        entityId: activity.id,
        entityType: EntityType.ACTIVITY,
      });
    },
    [activity, showEntityView]
  );

  if (!value || dateToDisplay === "") {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return <ButtonLink onClick={handleSelect}>{dateToDisplay}</ButtonLink>;
};

const mapDispatchToProps = {
  showEntityView,
};

export default connect(null, mapDispatchToProps)(ActivityAnnotationLinkCellRenderer);
